/**
 * Notification preference catalogs shared by Settings.tsx and Notifications.tsx.
 *
 * Until v1.14 these arrays were duplicated across both pages, which was the
 * exact source of bugs like "Claude Workspace toggles only render in Settings".
 * Keep this file as the single source of truth — both pages import from here.
 */

export interface PlatformNotificationType {
  type: string
  label: string
  description?: string
}

export interface UserNotificationType {
  type: string
  label: string
  highPriority?: boolean
}

export interface AdminMonitoringType {
  type: string
  label: string
  description: string
  highPriority?: boolean
}

export const PLATFORM_NOTIFICATION_TYPES: PlatformNotificationType[] = [
  {
    type: 'changelog_released',
    label: 'New changelog releases',
    description: 'Get notified when a new platform release is published',
  },
  {
    type: 'new_public_app',
    label: 'New public apps in Browse',
    description: 'Get notified when someone publishes a new app to Browse',
  },
]

export const USER_NOTIFICATION_TYPES: UserNotificationType[] = [
  { type: 'app_shared', label: 'App shared with you' },
  { type: 'access_request', label: 'Access request on your app' },
  { type: 'access_approved', label: 'Access request approved' },
  { type: 'access_denied', label: 'Access request denied' },
  { type: 'public_access_approved', label: 'Your public-access request was approved' },
  { type: 'public_access_rejected', label: 'Your public-access request was rejected' },
  { type: 'production_build_triggered', label: 'App publish started' },
  { type: 'production_build_success', label: 'App published successfully' },
  { type: 'production_build_failed', label: 'App publish failed', highPriority: true },
  { type: 'app_error', label: 'App error', highPriority: true },
  { type: 'app_auto_stopped', label: 'App auto-stopped' },
  { type: 'feedback_comment', label: 'Comment on your feedback' },
  { type: 'feedback_status', label: 'Feedback status changed' },
  { type: 'ownership_transfer_request', label: 'Someone wants to transfer app ownership to you' },
  { type: 'ownership_transfer_responded', label: 'Your ownership transfer was accepted or declined' },
]

export const CLAUDE_NOTIFICATION_TYPES: UserNotificationType[] = [
  { type: 'claude_awaiting_input', label: 'Claude is waiting for input', highPriority: true },
  { type: 'claude_task_complete', label: 'Claude finished a task' },
]

export const ADMIN_NOTIFICATION_TYPES: UserNotificationType[] = [
  { type: 'new_user_registered', label: 'New user registered' },
  { type: 'secret_scan_finding', label: 'Secret scan finding', highPriority: true },
  { type: 'new_feedback', label: 'New feedback submitted' },
  { type: 'public_access_requested', label: 'Public-access requested on an app', highPriority: true },
]

export const ADMIN_MONITORING_TYPES: AdminMonitoringType[] = [
  {
    type: 'other_app_errors',
    label: "App errors on other users' apps",
    description: "Get notified when apps you don't own encounter errors",
    highPriority: true,
  },
  {
    type: 'other_app_published',
    label: "New apps published by other users",
    description: 'Get notified the first time another user successfully publishes a new app, regardless of sharing mode',
  },
]
